import { blocksToMd } from '@w/content-blocks/src/serializer'
import { diffClass } from './review/views'
import { selectComment, selectReviewChange, clearReviewChangeHighlights, NestedCodeSourceEditor } from './review'
import { NestedEditorProvider, type NestedCodeEditorProps } from '../nested-editor-ctx'
import { UFile, ulid } from '@w/utils'
import { insertBlocks } from '../doc-bindings-for-page'
import { fileUploadFailedError } from '../errors'
import { BlockViewProvider } from './blocks/services'
import { enterScope, liveScope, sameScope, scopeAddress, scopeBlocks } from '../core/scope'
import { beginOperation, replaceBlock as replaceOp } from '../core/ops'
import { ReviewProvider } from './review/context'
import { MobileReviewProvider } from './review/mobile-review-panel'
import { blockViews as reviewBlockViews } from './review'
import { recordHistory } from '../core/history'
import { canonicalBlocks } from '../core/canonical-blocks'
import { uploadForOwner, type UploadCallbacks } from '../upload'
import { indexBlocks, indexViewBlocks } from '../doc-blocks'
import { prepareQuestionMetadata } from '../question-metadata'
import { writeDocument } from '../write-document'
import { PAGE_TITLE_BLOCK_ID } from '../page-title-id'
import { PageCommentAction, PageComments } from '../page-comments'
import { useMediaQuery, withoutActWarning } from '@w/utils-fe'
import type { BlockEditorHandle, BlockEditorProps } from '../types/block-editor.types'
import { EditorContext, publishSelectionAnchors, remoteSelectionsStore, withEditorState } from '../editor-state'
import { EditorModeProvider, ClozeHintFailProvider, AnswerPracticeProvider, type PracticeLayout } from '../editor-mode-ctx'
import { EditorLayoutContext } from '../editor-layout'
import { MediaUrlProvider } from '../media-url-ctx'
import { PageInfoProvider } from '../page-info-ctx'
import { pageDocument } from '../doc-blocks'
import { DEFAULT_EDITOR_TEST_IDS } from '../editor-test-ids'
import { blockEditorTheme } from '../theme'
import { contentBlocksEditorI18n } from '../i18n'
import type { CoreViewApi } from './view-api'
import { blockViews } from './blocks'
import { renderParagraph } from './blocks/structure'
import { ToggleList } from './blocks/toggle'
import { overlays } from './overlays'
import { overlays as reviewOverlays } from './review'
import { highlightSpeech } from './speech-highlight'
import { RemoteSelections } from './remote-selections'
import { anchorComposition, composedEdits, compositionLive, type CompositionAnchors } from './composition'
import { tableGrid } from '@w/content-blocks/src/table-html'
import { corePoint, domChildren, domPoint, selectedCellIds } from './dom-selection'
import { expandTableRange } from '../core/selection'
import { anchoredPoint, assertLiveSelection, liveSelection, restoredSelection, saveSelection, textAnchor, writeSelection, type SavedSelection } from '../core/state'
import { wordSegments } from '../core/chars'
import { findBlock, textChange } from './editing'
import { questionIds } from '../core/unsaved-copy'
import { clipboardCut, clipboardData, copiedData, pasteIntent, selectedFragment } from '../core/clipboard'
import { textDropSide } from '../core/drop'
import { blockRange, draggedBlocks } from '../core/block-selection'
import { createElement, Fragment, memo, useContext, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState,
  type ReactNode, type Ref } from 'react'
import { type Block } from '@w/content-blocks'
import { applyIntent, blockClass, selectedBlocks, type Composition, type CorePoint, type CoreState, type EditorIntent } from '../core'
import { ATOM, canonicalPoint, children, gapOf, gapPoint, isAtom, isListed, isTextRow, locate, plain, pointAt, rows } from '../core/tree-read'
import type { CoreResult } from '../core/apply-intent'
import { crdt, type Doc, type Snapshot, type Version } from '@w/crdt'
import { createCoreState, importRemote } from '../core/state'
import { checkboxKey } from '../core/checklist'
import { toggleHeaderItem } from '../core/toggle'
import { resolveKey } from '../core/keymap'
import { viewPlatform } from './view-platform'
import { Checkbox } from '@w/ui/components/checkbox'

export const blockTitles = {
  Paragraph: 'paragraph', 'Heading 1': 'h1', 'Heading 2': 'h2', 'Heading 3': 'h3',
  Quote: 'quote', 'Code Block': 'code', 'Bullet List': 'bullet',
  'Numbered List': 'number', 'Check List': 'check',
} as const

export interface CoreEditorHandle extends BlockEditorHandle {
  /** Test harness observes authored and remote transactions; product state uses useEditorState. */
  observeCommits: (listener: (origin: 'local' | 'remote') => void) => () => void
  resetDocument: (doc: Doc, preserveViews?: boolean) => void
  state: () => CoreState
  formatBlock: (block: Extract<EditorIntent, { type: 'formatBlock' }>['block']) => void
  moveBlock: (source: string, target: string, placement: 'before' | 'after' | 'inside') => void
  syncSelection: () => void
  /** The active IME session's marked text; the Doc never contains it. */
  composition: () => Composition | null
}

export type CoreEditorProps = Omit<BlockEditorProps, 'onChange' | 'fileHandler' | 'spaceId'> &
  Partial<Pick<BlockEditorProps, 'onChange' | 'fileHandler' | 'spaceId'>> & {
    onStateChange?: (state: CoreState) => void
    ref?: Ref<CoreEditorHandle>
  }

// Render functions return elements: calling a component directly would run its hooks inside the caller.
const renderNestedCode = (props: NestedCodeEditorProps) => <NestedCodeSourceEditor {...props} />

/** A top-level `p` or `h` holding only text: renderBlock's output for it reads nothing but the block and the props
 * compared here (no selection, no other block), so a keystroke re-renders only the block it edits. The comparator
 * leaves out `render` on purpose: renderBlock is recreated on every CoreEditor render. */
interface TextRowProps { block: Block; mode: string; viewMode: string; listed: boolean; inDocument: boolean; render: (block: Block) => ReactNode
  /** Answer practice marks: a revealed cloze changes its paragraph's tag (renderParagraph). */
  practiceMarks?: Readonly<Record<string, number | null>> }
const TextRow = memo(function TextRow(props: TextRowProps) { return props.render(props.block) }, (a, b) => a.block === b.block && a.mode === b.mode &&
  a.viewMode === b.viewMode && a.listed === b.listed && a.inDocument === b.inDocument && a.practiceMarks === b.practiceMarks)

const defaultFileHandler = {
  getUrl: (src: string) => src,
  upload: () => Promise.reject(new Error('File upload unavailable')),
}

export function CoreEditor(props: CoreEditorProps) {
  const parentEditor = useContext(EditorContext)
  const { doc, onStateChange, onChange, ref, onReady, focusOnMount, registerQuestionTarget, mode = 'edit', fileHandler = defaultFileHandler } = props
  const titleProps = useRef(props)
  titleProps.current = props
  const smallViewport = useMediaQuery('(max-width: 1025px)')
  const layout = props.layout ?? (smallViewport ? 'touch' : 'desktop')
  const testIds = { ...DEFAULT_EDITOR_TEST_IDS, ...props.testIds }
  const [editable, setEditable] = useState(true)
  const [documentEpoch, setDocumentEpoch] = useState(0)
  const [mediaPreviews, setMediaPreviews] = useState<Record<string, string>>({})
  const [internalPageCommentsComposing, setInternalPageCommentsComposing] = useState(false)
  const pageCommentsComposing = props.pageComments?.composer?.open ?? internalPageCommentsComposing
  const setPageCommentsComposing = props.pageComments?.composer?.onOpenChange ?? setInternalPageCommentsComposing
  const [rootElement, setRootElement] = useState<HTMLDivElement | null>(null)
  const [state, setState] = useState<CoreState>(() => createCoreState(doc))
  const commitObservers = useRef(new Set<(origin: 'local' | 'remote') => void>())
  const current = useRef(state)
  const clipboardCommand = useRef<{ cut?: string; paste?: string } | null>(null)
  /** The last keydown was Mod+Shift+V: the next paste takes the clipboard's plain text literally. */
  const pastePlain = useRef(false)
  const uploadEpoch = useRef(new AbortController())
  const root = useRef<HTMLDivElement>(null)
  /** Lexical's block cursor span, when rendered: read by the selection effect without scanning the root per commit. */
  const lexicalCursor = useRef<HTMLSpanElement>(null)
  const blockRevealCleanup = useRef<() => void>(() => {})
  const blockRevealQueue = useRef<{ blockId?: string; toggleItems: ReadonlySet<string> }[]>([])
  const [revealRequests, setRevealRequests] = useState<{ blockId?: string; toggleItems: ReadonlySet<string> }[]>([])
  function revealBlock(blockId?: string): boolean {
    blockRevealCleanup.current()
    const element = blockId && [...(root.current?.querySelectorAll<HTMLElement>('[data-block-id]') ?? [])]
      .find(element => element.dataset.blockId === blockId)
    const toggleItems = new Set<string>()
    if (element) {
      // Resolve rendered containment once, rather than searching the container again in each item.
      for (let parent = element.parentElement; parent && parent !== root.current; parent = parent.parentElement) {
        if (parent.dataset.toggleBody) toggleItems.add(parent.dataset.toggleBody)
      }
      for (const control of element.querySelectorAll<HTMLElement>('[data-toggle-control]')) {
        if (control.dataset.toggleControl) toggleItems.add(control.dataset.toggleControl)
      }
    }
    const request = { blockId: element ? blockId : undefined, toggleItems }
    blockRevealQueue.current.push(request)
    setRevealRequests(blockRevealQueue.current.slice())
    return !!element
  }
  // Child disclosures commit before this layout effect scrolls to the now-visible target.
  useLayoutEffect(() => {
    blockRevealQueue.current = []
    for (const request of revealRequests) {
      blockRevealCleanup.current()
      blockRevealCleanup.current = revealLinkedBlock(root.current, request.blockId)
    }
  }, [revealRequests, blockRevealQueue, blockRevealCleanup, root])
  const surface = useRef<HTMLDivElement>(null)
  // Stable ref attachment keeps the root available to child layout effects that place field focus.
  const [attachRoot] = useState(() => (element: HTMLDivElement | null) => { root.current = element; setRootElement(element) })
  const restoring = useRef(false)
  const renderedState = useRef<{ state: CoreState; version: Version } | null>(null)
  // A remote import was committed after React last rendered: the DOM still shows the text before it, so a DOM
  // offset names a point of the old text (decisions.md:160 — the caret follows peer edits through text cursors).
  const importedSinceRender = useRef(false)
  /** The rendered doc state, kept at the first remote import after a render (the doc is still the rendered one
   * then): native-input reconcile maps offsets typed over the rendered text through it. crdt_rs keeps no delete
   * history, so a past version cannot be checked out later. */
  const renderedSnapshot = useRef<Snapshot | null>(null)
  const column = useRef<number | null>(null)
  const dragState = useRef<{ block?: string; source?: SavedSelection }>({})
  const tableDragging = useRef(false)
  /**
   * Active IME session. The marked text is DOM-owned until the session ends (ProseMirror `view.composing`): reconcile and
   * selection sync are suspended, and the top-level block that owns it renders as it was at compositionstart (`frozen`),
   * so a remote import never rewrites the marked Text node. `container` is the leaf (or empty row) holding the marked
   * Text node; `texts` snapshots its Text nodes so React's owned DOM is restored before the commit; `anchors` place the
   * commit in the Doc, so peer edits move it (ime-ownership-design.md).
   */
  const composing = useRef<{ session: Omit<Composition, 'text'>; container: HTMLElement; original: string
    startInContainer: number; texts: Map<Text, string>; anchors: CompositionAnchors } | null>(null)
  const [frozen, setFrozen] = useState<Block | null>(null)
  /** The editor ended a session the IME still holds open: its composition input is dropped until its compositionend. */
  const released = useRef(false)
  /** The owner a late compositionend found diverged from its render: it mounts again (a new key) to drop the IME's DOM. */
  const [remounted, setRemounted] = useState<{ id: string; count: number } | null>(null)
  const mutations = useRef<MutationObserver | null>(null)
  const ending = useRef<(how: 'finish' | 'cancel') => void>(() => {})
  function markedText(): string {
    const active = composing.current
    if (!active) return ''
    const now = active.container.textContent
    const length = now.length - active.original.length + active.session.replaced
    return now.slice(active.startInContainer, active.startInContainer + length)
  }
  /**
   * Ends the IME session once (a no-op without one): restores React's DOM, then commits the marked text where its anchors
   * are now as one history step, or nothing when cancelled or no longer valid; the owner thaws either way. `ended`: the
   * browser's compositionend ended it; otherwise the editor did (a command, blur, reset) and the IME's late input for this
   * session is dropped. `time`: the ending event's clock, so a commit can merge with typing into one history entry.
   */
  function endComposition(how: 'finish' | 'cancel', ended = false, time?: number) {
    const active = composing.current
    if (!active) return
    const text = markedText()
    const dom = window.getSelection()
    const inside = ended && dom?.anchorNode && dom.focusNode && active.container.contains(dom.anchorNode) && active.container.contains(dom.focusNode)
      ? { from: containerLength(active.container, dom.anchorNode, dom.anchorOffset) - active.startInContainer,
        to: containerLength(active.container, dom.focusNode, dom.focusOffset) - active.startInContainer }
      : null
    composing.current = null
    released.current = !ended
    // Restore React's owned text before the commit replaces the marked range.
    for (const [node, data] of active.texts) if (node.data !== data) node.data = data
    const walker = document.createTreeWalker(active.container, NodeFilter.SHOW_TEXT)
    const added: Text[] = []
    for (let node = walker.nextNode(); node; node = walker.nextNode()) if (node instanceof Text && !active.texts.has(node)) added.push(node)
    for (const node of added) node.remove()
    mutations.current?.takeRecords()
    setFrozen(null)
    if (how === 'cancel' || mode !== 'edit' || !editable) return
    column.current = null
    const at = composedEdits(current.current, active.anchors, text, (state, intent, timing) => {
      commit(applyIntent(state, intent, timing))
      return current.current
    }, time)
    // The caret inside the marked text carries to the same offsets in the committed text, unless a shortcut consumed it.
    const committed = at && rows(current.current.blocks).find(entry => entry.block.id === at.row)
    if (inside && at && committed && plain(committed.block).slice(at.offset, at.offset + text.length) === text &&
      Math.max(inside.from, inside.to) <= text.length && Math.min(inside.from, inside.to) >= 0) {
      commit({ state: writeSelection(current.current, { anchor: pointAt(committed, at.offset + inside.from),
        focus: pointAt(committed, at.offset + inside.to) }), change: null })
    }
  }
  function commit(result: CoreResult, origin: 'local' | 'remote' = 'local') {
    assertLiveSelection(result.state)
    if (result.state.selection) result.state.selection = expandTableRange(result.state.blocks, result.state.selection)
    current.current = result.state
    setState(result.state)
    if (origin === 'remote') importedSinceRender.current = true
    if (result.change && mode === 'edit' && editable) onChange?.(result.change, clipboardCommand.current ?? undefined)
    onStateChange?.(result.state)
    for (const listener of commitObservers.current) listener(origin)
  }
  function syncSelection() {
    if (mode === 'review' || restoring.current || composing.current || !root.current) return
    if (importedSinceRender.current) return
    if (document.activeElement?.matches('input, textarea, select')) return
    const dom = window.getSelection()
    if (!dom?.anchorNode || !dom.focusNode || !root.current.contains(dom.anchorNode) || !root.current.contains(dom.focusNode)) return
    const anchorElement = dom?.anchorNode instanceof Element ? dom.anchorNode : dom?.anchorNode?.parentElement
    const focusElement = dom.focusNode instanceof Element ? dom.focusNode : dom.focusNode.parentElement
    if (anchorElement?.closest('[data-content-blocks-editor-surface]') !== root.current.parentElement || focusElement?.closest('[data-content-blocks-editor-surface]') !== root.current.parentElement) return
    if (anchorElement?.closest('[data-core-field]')) return
    const field = anchorElement?.closest<HTMLElement>('[data-core-scope]')
    const address = field?.dataset.coreScope
    const entersScope = address !== (current.current.scope && scopeAddress(current.current.scope))
    // A node selection holds until the DOM selection moves into another editing scope (a nested field).
    if (current.current.selection?.nodes?.length && !entersScope) return
    if (entersScope) {
      const scope = address ? enterScope(current.current.doc, address) : undefined
      current.current = { ...current.current, scope, blocks: scopeBlocks(current.current.doc, scope), selection: null, pendingFormat: undefined }
    }
    const selectionRoot = field ?? root.current
    const domAnchor = corePoint(selectionRoot, dom?.anchorNode ?? null, dom?.anchorOffset ?? 0, current.current.blocks)
    const domFocus = dom?.anchorNode === dom?.focusNode && dom?.anchorOffset === dom?.focusOffset
      ? domAnchor && { ...domAnchor }
      : corePoint(selectionRoot, dom?.focusNode ?? null, dom?.focusOffset ?? 0, current.current.blocks)
    if (domAnchor && domFocus) {
      // A DOM child gap inside a row becomes the canonical leaf-anchored point.
      const anchor = canonicalPoint(current.current.blocks, domAnchor)
      const focus = canonicalPoint(current.current.blocks, domFocus)
      for (const [point, node] of [[anchor, dom?.anchorNode], [focus, dom?.focusNode]] as const) {
        // The point's leaf is usually the DOM endpoint's ancestor: `closest` walks the depth, while an attribute-value
        // `querySelector` scans the page in document order (thousands of elements per keystroke mid-page).
        const selector = `[data-block-id="${point.blockId}"]`
        const near = (node instanceof Element ? node : node?.parentElement)?.closest<HTMLElement>(selector)
        const leaf = near && root.current.contains(near) ? near : root.current.querySelector<HTMLElement>(selector)
        const annotation = leaf?.closest('[data-cloze]')
        if (annotation && node && !annotation.contains(node)) point.affinity = 'outside'
      }
      const previous = current.current.selection
      if (previous?.anchor.blockId === anchor.blockId && previous.anchor.offset === anchor.offset &&
        previous.focus.blockId === focus.blockId && previous.focus.offset === focus.offset &&
        previous.anchor.affinity === anchor.affinity && previous.focus.affinity === focus.affinity) return
      if (previous?.table && !gapOf(current.current.blocks, anchor) && !gapOf(current.current.blocks, focus)) {
        const entries = rows(current.current.blocks)
        const same = (left: CorePoint, right: CorePoint) => {
          const a = locate(entries, left)
          const b = locate(entries, right)
          return a.row === b.row && a.offset === b.offset
        }
        if (same(previous.anchor, anchor) && same(previous.focus, focus)) return
      }
      column.current = null
      const selection = expandTableRange(current.current.blocks, { anchor, focus })
      const entries = rows(current.current.blocks)
      const row = entries[locate(entries, anchor).row]
      const table = row?.parents.find((parent) => parent.type === 'table')
      const tr = row?.parents.find((parent) => parent.type === 'tr')
      if (row && table?.type === 'table' && tr) {
        const grid = tableGrid(table)
        const y = grid.findIndex((cells) => cells.some((cell) => cell.id === row.block.id))
        selection.cell = { tableId: table.id,
          x: grid[y].findIndex((cell) => cell.id === row.block.id), y }
      }
      const next = writeSelection(current.current, selection)
      current.current = next
      setState(next)
    }
  }
  function editingRoot() {
    const scope = current.current.scope
    return scope ? [...root.current?.querySelectorAll<HTMLElement>('[data-core-scope]') ?? []]
      .find(element => element.dataset.coreScope === scopeAddress(scope)) ?? root.current : root.current
  }
  /** Applies `intent`; `time` (the input event's clock) lets consecutive typing merge into one history entry. */
  function dispatch(intent: EditorIntent, time?: number): CoreResult {
    // An application command acts on the document with the composed text in it.
    endComposition('finish', false, time)
    if ((mode !== 'edit' || !editable) && !['setSelection', 'moveCaret', 'selectAll'].includes(intent.type)) return { state: current.current, change: null }
    if (intent.type === 'moveCaret' && intent.unit === 'visualLine') {
      visual(intent.direction, intent.extend ?? false)
      return { state: current.current, change: null }
    }
    column.current = null
    // Disclosure is view-local (blocks/toggle.tsx): every Return route (keydown, beforeinput) tells core when the caret's
    // toggle body is hidden (notion-browser-queue.md 40).
    const item = intent.type === 'insertParagraph' ? toggleHeaderItem(current.current) : undefined
    const closed = item !== undefined && root.current?.querySelector(`[data-toggle-control="${CSS.escape(item)}"]`)
      ?.getAttribute('aria-expanded') === 'false'
    // Row and handle moves share this boundary. Select before core captures history, so undo and redo keep
    // the dragged blocks selected too. Copies retain core's first-copy caret behavior.
    const moved = intent.type === 'dropBlocks' && !intent.copy ? selectedBlocks(current.current.blocks, intent.blocks) : []
    const edge = (block: Block) => ({ blockId: block.id, offset: 0 })
    const before = moved.length > 0 ? { ...current.current, selection: {
      anchor: edge(moved[0]), focus: edge(moved.at(-1) ?? moved[0]), nodes: moved.map(block => block.id),
    } } : current.current
    const result = applyIntent(before, closed ? { type: 'insertParagraph', collapsed: true } : intent, { time })
    // Invalid and unchanged drops must not replace the old selection or its history.
    if (before !== current.current && !result.change) return { state: current.current, change: null }
    commit(result)
    return result
  }
  function visual(direction: 'up' | 'down' | 'backward' | 'forward', extend: boolean) {
    const previous = current.current
    if (!previous.selection) return
    if (previous.selection.nodes?.length) {
      dispatch({ type: 'moveCaret', unit: 'character', direction: direction === 'up' || direction === 'backward' ? 'backward' : 'forward', extend })
      return
    }
    const entries = rows(previous.blocks)
    const focusGap = gapOf(previous.blocks, previous.selection.focus)
    if (focusGap?.siblings === previous.blocks && (direction === 'up' || direction === 'down')) {
      const index = focusGap.index - (direction === 'up' ? 1 : 0)
      const block = previous.blocks[index]
      if (block?.type === 'table') {
        const cells = rows([block])
        const first = cells[0]
        const last = cells.at(-1)
        if (!first || !last) return
        const focus = pointAt(direction === 'up' ? first : last,
          direction === 'up' ? 0 : plain(last.block).length)
        const selection = { anchor: extend ? previous.selection.anchor : focus, focus }
        if (extend && previous.blocks.length === 1) {
          const end = { x: children(children(block).at(-1) ?? block).length - 1,
            y: children(block).length - 1 }
          commit({ state: { ...previous, selection: { ...selection,
            table: { tableId: block.id, anchor: { x: 0, y: 0 }, focus: end } } }, change: null })
        } else commit({ state: { ...previous, selection }, change: null })
        return
      }
    }
    const position = locate(entries, previous.selection.focus)
    const currentRow = entries[position.row]
    // Ask native layout before interpreting a vertical key as a block-boundary crossing.
    // A wrapped line (including a wrapped table cell) is still inside this row.
    const dom = window.getSelection()
    const selectionRoot = editingRoot()
    const native = dom && typeof dom.modify === 'function' && dom.focusNode && selectionRoot?.contains(dom.focusNode)
    if (native && selectionRoot) {
      dom.modify(extend ? 'extend' : 'move', direction === 'up' || direction === 'backward' ? 'backward' : 'forward',
        direction === 'up' || direction === 'down' ? 'line' : 'lineboundary')
      const point = corePoint(selectionRoot, dom.focusNode, dom.focusOffset, previous.blocks)
      const target = point && locate(entries, point)
      // A sole noneditable inline control can have no native line-boundary caret.
      // Its row has exactly two core stops; retain native layout for mixed/wrapped text.
      if ((direction === 'backward' || direction === 'forward') &&
        target?.row === position.row && target.offset === position.offset &&
        plain(currentRow.block) === ATOM) {
        const focus = pointAt(currentRow, direction === 'backward' ? 0 : 1)
        commit({ state: { ...previous, selection: {
          anchor: extend ? previous.selection.anchor : focus, focus,
        } }, change: null })
        return
      }
      if (direction === 'backward' || direction === 'forward' ||
        target && target.row === position.row && target.offset !== position.offset) {
        syncSelection()
        return
      }
    }
    const topBlock = currentRow.parents[0] ?? currentRow.block
    const adjacent = previous.blocks[previous.blocks.indexOf(topBlock) + (direction === 'up' ? -1 : 1)]
    if ((direction === 'up' || direction === 'down') && adjacent?.type === 'hr' &&
      (direction === 'up' ? !plain(currentRow.block).slice(0, position.offset).includes('\n')
        : !plain(currentRow.block).slice(position.offset).includes('\n'))) {
      const point = pointAt(currentRow, direction === 'up' ? 0 : plain(currentRow.block).length)
      current.current = { ...previous, selection: { anchor: previous.selection.anchor, focus: point } }
      dispatch({ type: 'moveCaret', unit: 'character', direction: direction === 'up' ? 'backward' : 'forward', extend })
      return
    }
    const table = currentRow.parents.find((block) => block.type === 'table')
    if (extend && adjacent?.type === 'table' && (direction === 'up' || direction === 'down')) {
      if (direction === 'up') {
        const target = rows([adjacent])[0]
        if (target) {
          const focus = pointAt(target, 0)
          commit({ state: { ...previous, selection: { anchor: previous.selection.anchor, focus } }, change: null })
        }
      } else if (position.offset === plain(currentRow.block).length) {
        const anchor = entries[locate(entries, previous.selection.anchor).row]
        const selection = blockRange(previous.blocks, (anchor.parents[0] ?? anchor.block).id, adjacent.id)
        if (selection) commit({ state: { ...previous, selection }, change: null })
      } else {
        const focus = pointAt(currentRow, plain(currentRow.block).length)
        commit({ state: { ...previous, selection: { anchor: previous.selection.anchor, focus } }, change: null })
      }
      return
    }
    if (table && (direction === 'up' || direction === 'down')) {
      const tableEntries = entries.filter((entry) => entry.parents.includes(table))
      const tr = currentRow.parents.find((block) => block.type === 'tr')
      const width = tr ? children(tr).length : 1
      const next = tableEntries[tableEntries.indexOf(currentRow) + (direction === 'up' ? -width : width)]
      if (next) {
        const focus = pointAt(next, Math.min(position.offset, plain(next.block).length))
        commit({ state: { ...previous, selection: { anchor: extend ? previous.selection.anchor : focus, focus } }, change: null })
        return
      }
      if (direction === 'up' && previous.blocks[0] === table) {
        const point = gapPoint({ siblings: previous.blocks, index: 0 })
        commit({ state: { ...previous, selection: { anchor: extend ? previous.selection.anchor : point,
          focus: point } }, change: null })
        return
      }
      if (direction === 'down' && table === previous.blocks.at(-1)) {
        const point = gapPoint({ siblings: previous.blocks, index: previous.blocks.length })
        if (currentRow === tableEntries.at(-1) && position.offset === plain(currentRow.block).length) {
          commit({ state: { ...previous, selection: {
            anchor: extend ? previous.selection.anchor : point, focus: point } }, change: null })
        } else {
          current.current = { ...previous, selection: { anchor: point, focus: point } }
          dispatch({ type: 'insertParagraph' })
        }
        return
      }
      if (direction === 'down') {
        const last = tableEntries.at(-1)
        if (last) {
          const point = pointAt(last, plain(last.block).length)
          current.current = { ...previous, selection: { anchor: point, focus: point } }
          dispatch({ type: 'moveCaret', direction: 'forward', unit: 'character', extend })
          return
        }
      }
    }
    // Visual lines are the layout engine's: where the host moves a selection by line (Chrome), it moves it and the
    // result syncs back (Lexical LexicalSelection.ts `modify` calls `domSelection.modify`). Hosts without this
    // capability use logical lines below; the synthetic host supplies its logical layout through the same DOM API.
    if (native) {
      syncSelection()
      return
    }
    const lines = entries.flatMap((entry, row) => {
      let offset = 0
      return plain(entry.block).split('\n').map((text) => {
        const line = { row, offset, length: text.length }
        offset += text.length + 1
        return line
      })
    })
    const index = lines.findIndex((line) => line.row === position.row &&
      position.offset >= line.offset && position.offset <= line.offset + line.length)
    const line = lines[index]
    if (!line) return
    let target = line
    let offset: number
    if (direction === 'backward' || direction === 'forward') {
      offset = direction === 'backward' ? 0 : line.length
      column.current = null
    } else {
      column.current ??= position.offset - line.offset
      target = lines[Math.max(0, Math.min(lines.length - 1,
        index + (direction === 'up' ? -1 : 1)))]
      offset = target === line ? direction === 'up' ? 0 : line.length
        : Math.min(column.current, target.length)
    }
    const focus = pointAt(entries[target.row], target.offset + offset)
    commit({ state: writeSelection(previous, { anchor: extend ? previous.selection.anchor : focus, focus }), change: null })
  }
  const methods = {
    observeCommits(listener: (origin: 'local' | 'remote') => void) {
      commitObservers.current.add(listener)
      return () => { commitObservers.current.delete(listener) }
    },
    state: () => current.current,
    resetDocument: (nextDoc: Doc, preserveViews = false) => {
      uploadEpoch.current.abort()
      uploadEpoch.current = new AbortController()
      setMediaPreviews({})
      endComposition('cancel')
      const next = createCoreState(nextDoc)
      if (!preserveViews) setDocumentEpoch(epoch => epoch + 1)
      column.current = null
      restoring.current = false
      current.current = next
      setState(next)
    },
    setEditable,
    // Focus may sit in a rich field inside the root; blur whichever owned element holds it.
    blur: () => {
      const active = document.activeElement
      if (active instanceof HTMLElement && root.current?.contains(active)) active.blur()
    },
    focusTitle({ selectText }: { selectText: boolean }) {
      const title = root.current?.querySelector<HTMLElement>('[data-page-title], [data-testid="content-blocks-editor-page-title"]')
      const element = title ?? root.current
      if (!element) return
      element.focus()
      if (title) {
        const range = document.createRange()
        range.selectNodeContents(title)
        if (!selectText) range.collapse(false)
        window.getSelection()?.removeAllRanges()
        window.getSelection()?.addRange(range)
      } else {
        const first = rows(current.current.blocks)[0]
        if (!first) return
        commit({ state: writeSelection(current.current, { anchor: pointAt(first, selectText ? 0 : plain(first.block).length),
          focus: pointAt(first, plain(first.block).length) }), change: null })
      }
    },
    selectComment: (threadId: string) => selectComment(root.current, threadId),
    selectReviewChange: (blockId?: string) => {
      if (!blockId && root.current?.contains(window.getSelection()?.anchorNode ?? null)) window.getSelection()?.removeAllRanges()
      return selectReviewChange(root.current, blockId ?? null)
    },
    revealBlock,
    highlightSpeech: (target: Parameters<BlockEditorHandle['highlightSpeech']>[0]) => {
      const painted = highlightSpeech(root.current, target)
      if (!painted) return false
      const rect = (painted.word ?? painted.element).getBoundingClientRect()
      if (rect.top < 96 || rect.bottom > window.innerHeight - 160) painted.element.scrollIntoView({ block: 'center', behavior: 'smooth' })
      return true
    },
    getSnapshot: () => ({ blocks: pageDocument(current.current.doc).content.children as unknown as Block[],
      pageTitle: titleProps.current.pageTitle ?? pageDocument(current.current.doc).title }),
    applyRemote: (update: Parameters<BlockEditorHandle['applyRemote']>[0]) => {
      syncSelection()
      const previous = current.current
      const selection = previous.selection
      // A caret holds the character before it: a peer's run landing at the caret stays after it, so the user's typed run
      // keeps growing from their own last character instead of hopping past the peer's run and splitting it.
      const caret = selection && selection.anchor.blockId === selection.focus.blockId && selection.anchor.offset === selection.focus.offset
      const saved = selection && !selection.nodes && !selection.table ? [textAnchor(previous.doc, selection.anchor, caret ? 'left' : 'right'), textAnchor(previous.doc, selection.focus, caret ? 'left' : 'right')] : null
      if (!importedSinceRender.current) renderedSnapshot.current = previous.doc.encodeSnapshot()
      const { imported, state: next } = importRemote(previous, update)
      if (saved && selection) {
        const resolve = (value: (typeof saved)[number]): CorePoint | null => {
          return value ? anchoredPoint(previous.doc, value) : null
        }
        const anchor = resolve(saved[0]), focus = resolve(saved[1])
        if (next.selection) next.selection = { ...next.selection, anchor: anchor ?? next.selection.anchor, focus: focus ?? next.selection.focus }
      }
      // crdt_rs A1: the import made this replica move a peer's follower after a block it moved (Doc.applyUpdate
      // `repair`). It is a local change peers need to converge, so it goes out in every mode, not as a user edit, and
      // before the commit's observers, which see it as part of this import.
      if (imported.repair) onChange?.(imported.repair)
      commit({ state: next, change: null }, 'remote')
      // The composing row or its owner is gone (deleted, or moved into another block): the marked text has nowhere to go.
      const anchors = composing.current?.anchors
      if (anchors && !compositionLive(current.current, anchors)) endComposition('cancel')
      return imported
    },
    formatBlock: (block: Extract<EditorIntent, { type: 'formatBlock' }>['block']) => { dispatch({ type: 'formatBlock', block }) },
    moveBlock: (source: string, target: string, placement: 'before' | 'after' | 'inside') => { dispatch({ type: 'dropBlocks', blocks: [source], target, placement }) },
    syncSelection,
    composition: () => composing.current && { ...composing.current.session, text: markedText() },
  }
  const [handle] = useState(() => Object.assign(withEditorState(methods), methods))
  useLayoutEffect(() => { Object.assign(handle, methods) })
  useImperativeHandle(ref, () => handle, [handle])
  useLayoutEffect(() => {
    uploadEpoch.current = new AbortController()
    return () => { uploadEpoch.current.abort() }
  }, [])
  useEffect(() => {
    if (!rootElement) return () => {}
    return () => { clearReviewChangeHighlights(rootElement) }
  }, [rootElement])
  useEffect(() => { onReady?.(handle) }, [handle, onReady])
  // Every path that moves the selection (commit, DOM sync, remote import) renders `state`.
  useEffect(() => { publishSelectionAnchors(handle, state) }, [handle, state])
  useEffect(() => { if (current.current.doc !== doc) handle.resetDocument(doc, true) }, [doc, handle, current])
  useEffect(() => { if (focusOnMount) handle.focusTitle({ selectText: false }) }, [handle, focusOnMount])
  useEffect(() => {
    if (!registerQuestionTarget || !rootElement) return () => {}
    const targets = rootElement.querySelectorAll<HTMLElement>('[data-question-id], [data-qid], [data-flashcard-qid]')
    for (const target of targets) {
      const qid = target.dataset.questionId ?? target.dataset.qid ?? target.dataset.flashcardQid
      if (qid) registerQuestionTarget(qid, target)
    }
    return () => {
      for (const target of targets) {
        const qid = target.dataset.questionId ?? target.dataset.qid ?? target.dataset.flashcardQid
        if (qid) registerQuestionTarget?.(qid, null)
      }
    }
  })
  const api: CoreViewApi = { state, renderBlock: block => renderBlock(block), revealRequests, syncSelection, dispatch, dispatchWithHistory: (intent, group) => {
    endComposition('finish')
    const result = mode === 'edit' && editable ? applyIntent(current.current, intent, { group }) : { state: current.current, change: null }
    if (mode === 'edit' && editable) commit(result)
    return result
  }, mode: editable ? mode : 'read', layout, parentEditor, root: rootElement, t: contentBlocksEditorI18n.t,
    select: selection => {
      endComposition('finish')
      const previous = current.current
      const named = selection ? [...selection.nodes ?? [], selection.anchor.blockId, selection.focus.blockId] : []
      // No selection (a plain input took focus) or blocks outside the field: the field scope ends.
      const outside = previous.scope && (!selection || named.some(id => id !== 'root' && !findBlock(previous.blocks, id)))
      commit({ state: { ...previous, ...(outside ? { scope: undefined, blocks: scopeBlocks(previous.doc) } : {}), selection }, change: null })
    },
    restore: saved => {
      endComposition('finish')
      const next = liveSelection(current.current, saved)
      if (next) commit({ state: next, change: null })
      return next
    },
    replaceBlock: (id, replacement) => {
      if (mode !== 'edit' || !editable) return
      endComposition('finish')
      const previous = current.current
      const scoped = previous.scope && { ...previous, blocks: structuredClone(previous.blocks), selection: structuredClone(previous.selection) }
      const scopedBlock = scoped && findBlock(scoped.blocks, id)
      if (scoped && scopedBlock) {
        // Inside a field (including its synthetic row) the replacement is a core operation over the field's list.
        const draft = scoped
        const block = scopedBlock
        const change = previous.doc.edit(tx => {
          beginOperation(draft, tx)
          replaceOp(draft, block, canonicalBlocks([structuredClone({ ...replacement, id })])[0])
          prepareQuestionMetadata(tx)
        })
        const scope = liveScope(previous.doc, previous.scope)
        const next = { ...draft, scope, blocks: scopeBlocks(previous.doc, scope) }
        next.selection = scope ? restoredSelection(previous.selection, previous.blocks, next.blocks) : null
        recordHistory(previous, next, change, { type: 'formatBlock', block: 'paragraph' })
        commit({ state: next, change })
        return
      }
      const draft = { ...previous, blocks: structuredClone(scopeBlocks(previous.doc)) }
      const block = findBlock(draft.blocks, id)
      if (!block) return
      const change = previous.doc.edit(tx => {
        const questionsBefore = [...indexViewBlocks(tx.read('')).values()].filter(item => item.block.type === 'question' || item.block.type === 'cloze')
        for (const key of Object.keys(block)) Reflect.deleteProperty(block, key)
        Object.assign(block, canonicalBlocks([structuredClone(replacement)])[0])
        writeDocument(tx, draft.blocks, scopeBlocks(previous.doc))
        prepareQuestionMetadata(tx)
        const questionsAfter = [...indexViewBlocks(tx.read('')).values()].filter(item => item.block.type === 'question' || item.block.type === 'cloze')
        const surviving = questionsBefore.filter(item => questionsAfter.some(next => next.block.id === item.block.id))
        // Removing an annotation shifts sibling offsets without changing the order of surviving questions.
        // Keep their authored fractional indices; actual insertions/reorders still use metadata preparation.
        if (surviving.length === questionsAfter.length && surviving.every((item, index) => item.block.id === questionsAfter[index].block.id)) {
          for (const [index, item] of surviving.entries()) {
            if (typeof item.block.fractionalIndex === 'string') tx.set(questionsAfter[index].path, 'fractionalIndex', item.block.fractionalIndex)
          }
        }
      })
      const scope = liveScope(previous.doc, previous.scope)
      const next = { ...draft, scope, blocks: scopeBlocks(previous.doc, scope) }
      const first = rows(next.blocks).find(row => row.block.id === id || row.parents.some(parent => parent.id === id))
      if (first) { const point = pointAt(first, 0); next.selection = { anchor: point, focus: point } }
      else next.selection = previous.scope && !scope ? null : restoredSelection(previous.selection, previous.blocks, next.blocks)
      recordHistory(previous, next, change, { type: 'formatBlock', block: 'paragraph' })
      commit({ state: next, change })
    },
    onCopyBlockLink: props.onCopyBlockLink, searchPages: props.searchPages, resolvePage: props.resolvePage, onOpenPage: props.onOpenPage,
    spaceId: props.spaceId, slashMenuItems: props.slashMenuItems, fileHandler, testIds, placeholder: props.placeholder }
  useEffect(() => {
    handle.revealBlock(props.linkedBlockId)
    return () => { blockRevealCleanup.current() }
  }, [props.linkedBlockId, handle, blockRevealCleanup])
  function upload(files: UFile[], callbacks: UploadCallbacks) {
    const epoch = uploadEpoch.current
    return uploadForOwner(fileHandler, files, epoch.signal, callbacks)
  }
  async function uploadFiles(files: File[]) {
    if (mode !== 'edit' || !editable || files.length === 0) return
    const ids: string[] = []
    const uploadDoc = current.current.doc
    const epoch = uploadEpoch.current
    const previews = new Set<string>()
    try {
      await upload(files.map(file => new UFile(file)), { prepared: items => {
        if (items.length === 0) return
        const previous = current.current
        // Lexical INSERT_FILES_COMMAND: files append at the end of the document, wherever the caret is; it stays put.
        const change = uploadDoc.edit(tx => {
          ids.push(...insertBlocks(tx, 'content.children', pageDocument(uploadDoc).content.children.length, items.map(item => ({ type: item.mediaType, src: item.src,
            ...(item.mediaType === 'file' ? { name: item.name ?? '', size: item.size ?? 0 } : {}) }))))
        })
        const next = { ...previous, blocks: scopeBlocks(uploadDoc, previous.scope) }
        recordHistory(previous, next, change, { type: 'insertFromPaste', md: '' })
        for (const item of items) previews.add(item.src)
        // Upload callbacks run after awaits, outside any React event.
        withoutActWarning(() => {
          setMediaPreviews(before => Object.assign({}, before, Object.fromEntries(items.map(item => [item.src, item.$file]))))
          commit({ state: next, change })
        })
      }, completed: srcs => {
        const locations = indexViewBlocks(pageDocument(uploadDoc))
        if (!ids.some((id, index) => srcs[index] && locations.get(id)?.block.src !== srcs[index])) return
        const completed = uploadDoc.edit(tx => {
          const locations = indexViewBlocks(tx.read(''))
          for (const [index, id] of ids.entries()) {
            const location = locations.get(id)
            if (location && srcs[index] && location.block.src !== srcs[index]) tx.set(location.path, 'src', srcs[index])
          }
        })
        withoutActWarning(() => { commit({ state: { ...current.current, blocks: scopeBlocks(uploadDoc, current.current.scope) }, change: completed }) })
      } })
    } catch (error) {
      throw fileUploadFailedError(error instanceof Error ? error : new Error(String(error)))
    } finally {
      if (uploadEpoch.current === epoch) withoutActWarning(() => { setMediaPreviews(before => Object.fromEntries(Object.entries(before).filter(([src]) => !previews.has(src)))) })
    }
  }
  function updateBlock(id: string, patch: Record<string, unknown>) {
    if (mode !== 'edit' || !editable) return
    endComposition('finish')
    if (id === PAGE_TITLE_BLOCK_ID && props.onPageTitleChange) {
      props.onPageTitleChange(typeof patch.title === 'string' ? patch.title : '')
      return
    }
    const previous = current.current
    const draft = { ...previous, blocks: structuredClone(scopeBlocks(previous.doc)) }
    const block = indexBlocks({ content: { children: draft.blocks } } as unknown as Parameters<typeof indexBlocks>[0]).get(id)?.block
    const original = id === PAGE_TITLE_BLOCK_ID ? { title: pageDocument(previous.doc).title } : block
    if (original && Object.entries(patch).every(([key, value]) => JSON.stringify(original[key as keyof typeof original]) === JSON.stringify(value))) return
    const change = previous.doc.edit(tx => {
      if (id === PAGE_TITLE_BLOCK_ID || (block?.type === 'pageMeta' && 'title' in patch)) tx.set('', 'title', typeof patch.title === 'string' ? patch.title : '')
      if (block) {
        for (const [key, value] of Object.entries(patch)) {
          if (value === undefined) Reflect.deleteProperty(block, key)
          else Reflect.set(block, key, value)
        }
        writeDocument(tx, draft.blocks, scopeBlocks(previous.doc))
        prepareQuestionMetadata(tx)
      }
    })
    const scope = liveScope(previous.doc, previous.scope)
    const next = { ...draft, scope, blocks: scopeBlocks(previous.doc, scope) }
    next.selection = previous.scope && !scope ? null : restoredSelection(previous.selection, previous.blocks, next.blocks)
    recordHistory(previous, next, change, { type: 'insertText', text: '' })
    commit({ state: next, change })
  }
  useLayoutEffect(() => {
    const element = root.current
    if (!element) throw new Error('Editor root missing during mount')
    const selectedField = () => {
      if (importedSinceRender.current) return null
      const node = window.getSelection()?.anchorNode
      const field = (node instanceof Element ? node : node?.parentElement)?.closest<HTMLElement>('[data-core-field], [data-core-scope]')
      return field && element.contains(field) ? field : null
    }
    const relayFieldInput = (event: Event) => {
      const field = selectedField()
      if (field && event.target === element) field.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: event instanceof InputEvent ? event.inputType : '', data: event instanceof InputEvent ? event.data : null }))
    }
    element.addEventListener('input', relayFieldInput)
    const transpose = () => {
      const selection = current.current.selection
      if (!selection || selection.nodes?.length || selection.table) return
      const entries = rows(current.current.blocks)
      const position = locate(entries, selection.focus), row = entries[position.row]
      if (!row) return
      const segments = [...new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(plain(row.block))]
      if (segments.length < 2) return
      const boundary = segments.findIndex(segment => segment.index >= position.offset)
      const right = boundary < 0 ? segments.length - 1 : Math.max(1, boundary)
      const left = segments[right - 1], next = segments[right]
      current.current = { ...current.current, selection: {
        anchor: pointAt(row, left.index), focus: pointAt(row, next.index + next.segment.length),
      } }
      dispatch({ type: 'insertText', text: next.segment + left.segment }, Date.now())
    }
    const input = (event: InputEvent) => {
      if (event.target instanceof Element && event.target.closest('input, textarea, select')) return
      const field = selectedField()
      if (field && event.target === element) {
        const forwarded = new InputEvent('beforeinput', { bubbles: true, cancelable: true, inputType: event.inputType, data: event.data, isComposing: event.isComposing })
        field.dispatchEvent(forwarded)
        if (forwarded.defaultPrevented) event.preventDefault()
        return
      }
      if (field?.dataset.coreField) return
      if (event.target instanceof Element && event.target.closest('[data-core-field]')) return
      if (event.defaultPrevented) return
      if (mode !== 'edit' || !editable) { event.preventDefault(); return }
      // Marked text stays DOM-owned until its session ends; the IME's input for a session the editor ended is dropped.
      const fromIme = event.isComposing || compositionInputs.has(event.inputType)
      if (fromIme && released.current) { event.preventDefault(); return }
      if (fromIme) return
      released.current = false
      // An application input during a session (the Edit menu's Undo) acts with the composed text in the document.
      endComposition('finish')
      syncSelection()
      // A node selection holds over its DOM rendering (its target range only approximates the selected blocks).
      const range = current.current.selection?.nodes?.length ? undefined : event.getTargetRanges?.()[0]
      if (range) {
        const anchor = corePoint(editingRoot() ?? element, range.startContainer, range.startOffset, current.current.blocks)
        const focus = corePoint(editingRoot() ?? element, range.endContainer, range.endOffset, current.current.blocks)
        if (anchor && focus) current.current = { ...current.current, selection: {
          anchor: canonicalPoint(current.current.blocks, anchor), focus: canonicalPoint(current.current.blocks, focus) } }
      }
      event.preventDefault()
      if (event.inputType === 'insertTranspose') {
        transpose()
      } else if (event.inputType === 'insertReplacementText') {
        dispatch({ type: 'insertText', text: [event.dataTransfer?.getData('text/plain'), event.data]
          .find((value) => !!value) ?? '' }, Date.now())
      } else if (event.inputType === 'insertText' || event.inputType === 'insertCompositionText') {
        dispatch({ type: 'insertText', text: event.data ?? '' }, Date.now())
      } else if (event.inputType === 'insertFromPaste') {
        dispatch({ type: 'insertFromPaste', md: event.dataTransfer?.getData('text/plain')
          ?? event.data ?? '' })
      } else if (event.inputType === 'insertLineBreak' && element.contentEditable === 'plaintext-only') {
        // A plaintext-only host reports a native Return as insertLineBreak (Chromium 156, Firefox 157), e.g. after a
        // keydown the keymap cannot resolve (IME, soft keyboards). Shift+Enter never gets here: the keymap owns and
        // prevents it at keydown (core/keymap.ts), so a beforeinput line break is Return (review-c1 report2).
        dispatch({ type: 'insertParagraph' }, Date.now())
      } else if (inputTypes.has(event.inputType)) {
        dispatch({ type: event.inputType } as EditorIntent, Date.now())
      }
    }
    const key = (event: KeyboardEvent) => {
      // The paste event after Mod+Shift+V cannot tell it from Mod+V (ProseMirror input.ts:662 tracks Shift the same way):
      // the keydown arms the paste it produces; key release, focus loss and composition disarm it (endPlainPaste).
      pastePlain.current = (event.metaKey || event.ctrlKey) && event.shiftKey && event.key.toLowerCase() === 'v' &&
        !event.isComposing
      const field = selectedField()
      if (field && event.target === element) {
        const forwarded = new KeyboardEvent('keydown', { key: event.key, code: event.code, bubbles: true, cancelable: true,
          ctrlKey: event.ctrlKey, metaKey: event.metaKey, shiftKey: event.shiftKey, altKey: event.altKey })
        field.dispatchEvent(forwarded)
        if (forwarded.defaultPrevented) event.preventDefault()
        return
      }
      if (event.defaultPrevented || event.target instanceof Element && event.target.closest('[data-core-field], input, textarea, select')) return
      // A focused control (Copy, a menu item) owns its activation keys, plain Enter and Space; editor shortcuts on it
      // (bulk toggle) still reach the keymap.
      if (event.target instanceof Element && event.target.matches('button, [role="button"], [role="menuitem"]') &&
        (event.key === 'Enter' || event.key === ' ') && !event.ctrlKey && !event.metaKey && !event.altKey && !event.shiftKey) return
      // A key inside an IME session is the IME's (UI Events: isComposing; key "Process", keyCode 229, while the IME
      // processes it).
      // oxlint-disable-next-line typescript/no-deprecated -- keyCode 229 is the one signal some IMEs send without isComposing or key "Process" (prosemirror-view input.ts:116); no standard replacement exists
      if (event.isComposing || event.key === 'Process' || event.keyCode === 229) return
      if (composing.current && resolveKey(viewPlatform(), event, current.current)) endComposition('finish')
      syncSelection()
      // cbe-core-keymap: every chord resolves through the core table; this view only applies motions in context.
      const binding = resolveKey(viewPlatform(), event, current.current)
      const motion = binding && 'motion' in binding ? binding.motion : undefined
      const intent = binding && 'intent' in binding ? binding.intent : undefined
      const selection = current.current.selection
      const entries = rows(current.current.blocks)
      const position = selection && locate(entries, selection.focus)
      const focused = document.activeElement instanceof HTMLElement &&
        document.activeElement.matches('li[role="checkbox"]') ? document.activeElement : null
      // The checkbox focus rule is core's (checklist.ts); this view only moves DOM focus.
      const action = checkboxKey(current.current, focused?.dataset.blockId ?? null, binding)
      if (action?.kind === 'toggle') {
        event.preventDefault()
        dispatch({ type: 'toggleTodo', item: action.item })
        return
      }
      if (action?.kind === 'focus') {
        event.preventDefault()
        const caret = action.caret
        if (caret) commit({ state: { ...current.current, selection: { anchor: caret, focus: caret } }, change: null })
        element.querySelector<HTMLElement>(`[data-block-id="${action.item}"][role="checkbox"]`)?.focus()
        return
      }
      if (focused) element.focus()
      if (action?.kind === 'release' && !action.keyMap) {
        event.preventDefault()
        return
      }
      if (motion === 'toggleAllDisclosures') {
        const controls = [...element.querySelectorAll<HTMLButtonElement>('[data-toggle-control]')]
        if (controls.length === 0) return
        event.preventDefault()
        const open = controls.some(control => control.getAttribute('aria-expanded') === 'false')
        // Close descendants first so the outermost header owns the final visible selection.
        for (const control of controls.toReversed()) {
          if ((control.getAttribute('aria-expanded') === 'true') !== open) control.click()
        }
        return
      }
      if (motion === 'toggleDisclosure') {
        const item = toggleHeaderItem(current.current)
        const control = item && [...element.querySelectorAll<HTMLButtonElement>('[data-toggle-control]')]
          .find(button => button.dataset.toggleControl === item)
        if (control) { event.preventDefault(); control.click() }
        return
      }
      if (motion === 'transpose' && position) { event.preventDefault(); transpose(); return }
      if (motion === 'toggleLink') {
        event.preventDefault()
        element.dispatchEvent(new CustomEvent('core-view-open-link'))
        return
      }
      if (intent) {
        event.preventDefault()
        dispatch(intent, Date.now())
      }
    }
    const dragStart = (event: DragEvent) => {
      syncSelection()
      const target = event.target instanceof Element ? event.target.closest('[data-block-id]') : null
      dragState.current.block = target instanceof HTMLElement && target.getAttribute('draggable') === 'true' ? target.dataset.blockId : undefined
      const dragged = current.current.selection
      // A text drag remembers its field: the drop may land in another container.
      dragState.current.source = dragged ? saveSelection({ ...current.current, selection: dragged }) : undefined
      if (dragState.current.block) event.dataTransfer?.setData('application/x-core-block', dragState.current.block)
      if (event.dataTransfer) {
        const native = document.createElement('div')
        const block = dragState.current.block && [...element.querySelectorAll<HTMLElement>('[data-block-id]')].find(node => node.dataset.blockId === dragState.current.block)
        if (block) native.append(block.cloneNode(true))
        else {
          const range = window.getSelection()?.rangeCount ? window.getSelection()?.getRangeAt(0) : undefined
          if (range) native.append(range.cloneContents())
        }
        // DOM question identifiers are presentation hooks, never external drag identity.
        for (const node of native.querySelectorAll('*')) for (const name of node.getAttributeNames()) {
          if (name.includes('qid') || name === 'data-testid') node.removeAttribute(name)
        }
        event.dataTransfer.setData('text/html', native.innerHTML)
      }

    }
    const dragOver = (event: DragEvent) => { event.preventDefault() }
    const drop = (event: DragEvent) => {
      if (event.target instanceof Element && event.target.closest('[data-core-field]')) return
      event.preventDefault()
      if (event.dataTransfer?.files.length) { void uploadFiles(Array.from(event.dataTransfer.files)); return }
      if (dragState.current.block) {
        const source = current.current.blocks.findIndex((block) => block.id === dragState.current.block)
        const target = event.target instanceof Element ? event.target.closest('[data-block-id]') : null
        const targetId = target instanceof HTMLElement ? target.dataset.blockId : undefined
        const index = current.current.blocks.findIndex((block) => block.id === targetId)
        if (source < 0 || index < 0 || source === index) return
        const rect = target?.getBoundingClientRect()
        const after = rect ? event.clientY >= rect.top + rect.height / 2 : false
        const dragged = draggedBlocks(current.current, current.current.blocks[source].id)
        if (dragged.includes(current.current.blocks[index].id)) return
        // One drag, one operation and one undo step.
        dispatch({ type: 'dropBlocks', blocks: dragged, target: current.current.blocks[index].id, placement: after ? 'after' : 'before', ...(event.altKey && { copy: true }) })
      } else {
        const range = (Reflect.get(element.ownerDocument, 'caretRangeFromPoint') as
          ((x: number, y: number) => Range | null) | undefined)?.call(element.ownerDocument, event.clientX, event.clientY)
        // The drop's container is the field under the pointer, not the one the drag started in.
        const hit = range?.startContainer
        const field = (hit instanceof Element ? hit : hit?.parentElement)?.closest<HTMLElement>('[data-core-scope]')
        const address = field && element.contains(field) ? field.dataset.coreScope : undefined
        const destination = address ? enterScope(current.current.doc, address) : undefined
        const destinationBlocks = scopeBlocks(current.current.doc, destination)
        const target = range && corePoint(field ?? element, range.startContainer, range.startOffset, destinationBlocks)
        if (!target) return
        const saved = dragState.current.source
        const source = saved && liveSelection(current.current, saved)
        const dragged = source && textDropSide(source.blocks, source.selection, source.selection?.anchor ?? target) !== null ? source : undefined
        // An internal drag whose text is gone (reset, undo, peer deletion) is cancelled, never replayed as an external drop.
        if (saved && !dragged) return
        // A move inside one container is the core's one-transaction text drop (nothing happens inside its own range).
        if (dragged && sameScope(dragged.scope, destination)) {
          current.current = dragged
          dispatch({ type: 'dropText', at: target }, Date.now())
          return
        }
        // Dropping a field into itself (its owner is part of the dragged range) changes nothing.
        if (dragged && destination && findBlock(selectedFragment(dragged).fragment, destination.blockId)) return
        // Across containers: delete the source, then insert at the drop; one history group from the dragged range to the drop.
        const group = `view:drag:${ulid()}`
        const run = (intent: EditorIntent) => { commit(applyIntent(current.current, intent, { group, time: Date.now() })) }
        const drop = saveSelection({ ...current.current, scope: destination, blocks: destinationBlocks, selection: { anchor: target, focus: target } })
        const data = event.dataTransfer ? clipboardData(event.dataTransfer) : {}
        const insert: EditorIntent = dragged ? { type: 'insertFromPaste', md: '', fragment: selectedFragment(dragged).fragment }
          : pasteIntent(data, () => questionIds(pageDocument(current.current.doc).content), props.spaceId)
        if (dragged) {
          current.current = dragged
          run({ type: 'deleteContentBackward' })
        }
        const into = liveSelection(current.current, drop)
        if (!into) return
        current.current = into
        run(insert)
      }
    }
    const dropListener = (event: DragEvent) => { drop(event) }
    const dragEnd = () => { dragState.current.block = undefined; dragState.current.source = undefined }
    element.addEventListener('dragstart', dragStart)
    element.addEventListener('dragover', dragOver)
    element.addEventListener('drop', dropListener)
    element.addEventListener('dragend', dragEnd)
    const paste = (event: ClipboardEvent) => {
      const field = selectedField()
      if (field && event.target === element && !event.clipboardData?.files.length) {
        const forwarded = new ClipboardEvent('paste', { bubbles: true, cancelable: true, clipboardData: event.clipboardData })
        field.dispatchEvent(forwarded)
        if (forwarded.defaultPrevented) event.preventDefault()
        return
      }
      if (event.target instanceof Element && event.target.closest('[data-core-field], input, textarea, select')) return
      syncSelection()
      event.preventDefault()
      if (event.clipboardData?.files.length) { void uploadFiles(Array.from(event.clipboardData.files)); return }
      if (event.clipboardData) {
        const data = clipboardData(event.clipboardData)
        clipboardCommand.current = { paste: clipboardCut(data.html, props.spaceId ?? '') }
        const plainPaste = pastePlain.current
        pastePlain.current = false
        // A plain paste takes text/plain alone, never the Markdown or HTML flavours; an empty one inserts nothing.
        const plain = plainPaste ? event.clipboardData.getData('text/plain') : ''
        try {
          if (!plainPaste) dispatch(pasteIntent(data, () => questionIds(pageDocument(current.current.doc).content), props.spaceId))
          else if (plain) dispatch({ type: 'insertFromPaste', md: plain, literal: true })
        } finally { clipboardCommand.current = null }
      }
    }
    const copy = (event: ClipboardEvent) => {
      if (event.target instanceof Element && event.target.closest('input, textarea, select')) return
      const field = selectedField()
      if (field && event.target === element) {
        const forwarded = new ClipboardEvent(event.type, { bubbles: true, cancelable: true, clipboardData: event.clipboardData })
        field.dispatchEvent(forwarded)
        if (forwarded.defaultPrevented) event.preventDefault()
        return
      }
      syncSelection()
      event.preventDefault()
      const { text, html } = copiedData(current.current, event.type === 'cut' ? 'cut' : 'copy', props.spaceId)
      if (!text) return
      if (html) event.clipboardData?.setData('text/html', html)
      event.clipboardData?.setData('text/markdown', text)
      // Plain text is what the reader sees selected: atom labels, not their Markdown identity. A caret selects nothing
      // visible, so its copy of the caret's block writes that block's Markdown, as Notion's copy handler does
      // (`- b` for a caret in bullet "b"; decisions.md "Notion clipboard round 2b").
      const { nodes, anchor, focus } = current.current.selection ?? {}
      const caret = !!anchor && !!focus && anchor.blockId === focus.blockId && anchor.offset === focus.offset
      const plainText = nodes?.length ? selectedBlocks(current.current.blocks, nodes).map(block => {
        const rendered = [...element.querySelectorAll<HTMLElement>('[data-block-id]')].find(node => node.dataset.blockId === block.id)
        if (!rendered) return plain(block)
        const clone = rendered.cloneNode(true) as HTMLElement
        for (const control of clone.querySelectorAll('button,input,select,textarea,[data-core-overlay],[aria-hidden="true"]')) control.remove()
        return clone.textContent
      }).join('\n') : current.current.selection?.table || caret ? text : window.getSelection()?.toString() ?? ''
      event.clipboardData?.setData('text/plain', plainText)
      if (event.type === 'cut') {
        clipboardCommand.current = { cut: clipboardCut(html, props.spaceId ?? '') }
        try { dispatch({ type: 'deleteContentBackward' }, Date.now()) }
        finally { clipboardCommand.current = null }
      }
    }
    const reconcile = () => {
      if (composing.current || released.current || mode !== 'edit' || !editable) return
      const rendered = renderedState.current
      // A native delta belongs to the Doc instance and identity epoch that rendered its text.
      if (!rendered || rendered.state.doc !== current.current.doc ||
        rendered.version.epoch !== current.current.doc.version().epoch) return
      let historical: Doc | undefined
      for (const leaf of element.querySelectorAll<HTMLElement>('[data-leaf="true"]')) {
        if (leaf.closest('[data-core-field]')) continue
        const scoped = leaf.closest<HTMLElement>('[data-core-scope]')
        if ((scoped?.dataset.coreScope ?? undefined) !== (current.current.scope && scopeAddress(current.current.scope))) continue
        const block = findBlock(current.current.blocks, leaf.dataset.blockId ?? '')
        if (!block) continue
        const remoteSelection = importedSinceRender.current
        const ownedBlock = remoteSelection ? findBlock(rendered.state.blocks, block.id) : block
        if (!ownedBlock) continue
        const before = plain(ownedBlock)
        const after = leaf.textContent ?? ''
        if (before === after) continue
        const change = textChange(before, after)
        let editAnchor = { blockId: block.id, offset: change.start }
        let editFocus = { blockId: block.id, offset: change.end }
        if (remoteSelection) {
          const snapshot = renderedSnapshot.current
          if (!snapshot) continue
          historical ??= crdt.load(snapshot)
          const start = textAnchor(historical, editAnchor, change.start === change.end ? 'left' : 'right')
          const end = change.start === change.end ? start : textAnchor(historical, editFocus, 'left')
          const anchor = start && anchoredPoint(current.current.doc, start)
          const focus = end && anchoredPoint(current.current.doc, end)
          // Retired or out-of-scope character targets never fall back onto current numeric offsets.
          if (!anchor || !focus || !findBlock(current.current.blocks, anchor.blockId) ||
            !findBlock(current.current.blocks, focus.blockId)) continue
          editAnchor = anchor
          editFocus = focus
        }
        observer.disconnect()
        // INV-DOMLAG: until the remote import renders, selection belongs to mapped core character anchors.
        const saved = remoteSelection ? current.current.selection : null
        const savedBlocks = current.current.blocks
        const caret = saved && saved.anchor.blockId === saved.focus.blockId && saved.anchor.offset === saved.focus.offset
        const anchors = saved && !saved.nodes && !saved.table
          ? [textAnchor(current.current.doc, saved.anchor, caret ? 'left' : 'right'),
            textAnchor(current.current.doc, saved.focus, caret ? 'left' : 'right')] : null
        const dom = remoteSelection ? null : window.getSelection()
        const nativeAnchor = dom && corePoint(element, dom.anchorNode, dom.anchorOffset)
        const nativeFocus = dom && corePoint(element, dom.focusNode, dom.focusOffset)
        const entries = rows(current.current.blocks)
        // Ordinary DOM offsets include the native edit; validate identity without the pre-edit text length bound.
        const anchorPosition = nativeAnchor && (nativeAnchor.blockId === 'root' ||
          findBlock(current.current.blocks, nativeAnchor.blockId)) && locate(entries, nativeAnchor)
        const focusPosition = nativeFocus && (nativeFocus.blockId === 'root' ||
          findBlock(current.current.blocks, nativeFocus.blockId)) && locate(entries, nativeFocus)
        const text = document.createTreeWalker(leaf, NodeFilter.SHOW_TEXT).nextNode()
        // Restore React's owned text before applying the narrow core edit.
        if (text instanceof Text) text.data = before
        current.current = { ...current.current, selection: {
          anchor: editAnchor,
          focus: editFocus,
        } }
        dispatch(change.text ? { type: 'insertText', text: change.text }
          : { type: 'deleteContentBackward' }, Date.now())
        if (remoteSelection) {
          const restored = restoredSelection(saved, savedBlocks, current.current.blocks)
          const anchor = anchors?.[0] && anchoredPoint(current.current.doc, anchors[0])
          const focus = anchors?.[1] && anchoredPoint(current.current.doc, anchors[1])
          commit({ state: { ...current.current, selection: restored && { ...restored,
            anchor: anchor ?? restored.anchor, focus: focus ?? restored.focus,
          } }, change: null })
        } else if (anchorPosition && focusPosition) {
          const updated = rows(current.current.blocks)
          commit({ state: { ...current.current, selection: {
            anchor: pointAt(updated[anchorPosition.row], anchorPosition.offset),
            focus: pointAt(updated[focusPosition.row], focusPosition.offset),
          } }, change: null })
        }
      }
    }
    const compositionStart = (event: CompositionEvent) => {
      if (event.target instanceof Element && event.target.closest('input, textarea, select')) return
      if (mode !== 'edit' || !editable) return
      released.current = false
      syncSelection()
      const selection = current.current.selection
      const dom = window.getSelection()
      if (!selection || selection.nodes?.length || selection.table || !dom?.anchorNode || !dom.focusNode) return
      // A session covers marked text inside one row; a cross-row selection is left to the platform.
      const anchors = anchorComposition(current.current, selection)
      if (!anchors) return
      const entries = rows(current.current.blocks)
      const [from, to] = [locate(entries, selection.anchor), locate(entries, selection.focus)]
        .toSorted((a, b) => a.row - b.row || a.offset - b.offset)
      const anchorElement = dom.anchorNode instanceof Element ? dom.anchorNode : dom.anchorNode.parentElement
      const container = anchorElement?.closest<HTMLElement>('[data-leaf="true"]') ??
        anchorElement?.closest<HTMLElement>('[data-core-scope]') ?? anchorElement?.closest<HTMLElement>('[data-block-id]')
      if (!container || !element.contains(container)) return
      const texts = new Map<Text, string>()
      const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT)
      for (let node = walker.nextNode(); node; node = walker.nextNode()) if (node instanceof Text) texts.set(node, node.data)
      const start = Math.min(containerLength(container, dom.anchorNode, dom.anchorOffset),
        containerLength(container, dom.focusNode, dom.focusOffset))
      composing.current = { session: { target: entries[from.row].block.id, start: from.offset, replaced: to.offset - from.offset },
        container, original: container.textContent, startInContainer: start, texts, anchors }
      setFrozen(scopeBlocks(current.current.doc).find(block => block.id === anchors.owner) ?? null)
    }
    const compositionEnd = (event: CompositionEvent) => {
      if (event.target instanceof Element && event.target.closest('input, textarea, select')) return
      if (composing.current) { endComposition('finish', true, Date.now()); return }
      if (!released.current) return
      released.current = false
      // The IME may have written its text into a block the editor already committed and re-rendered: mount it again.
      const rendered = renderedState.current?.state
      for (const leaf of element.querySelectorAll<HTMLElement>('[data-leaf="true"]')) {
        const block = rendered && findBlock(rendered.blocks, leaf.dataset.blockId ?? '')
        if (!block || plain(block) === leaf.textContent) continue
        const owner = scopeBlocks(rendered.doc).find(top => findBlock([top], block.id))
        if (owner) setRemounted(previous => ({ id: owner.id, count: (previous?.count ?? 0) + 1 }))
      }
    }
    const observer = new MutationObserver(reconcile)
    mutations.current = observer
    observer.observe(element, { characterData: true, subtree: true })
    ending.current = endComposition
    element.addEventListener('compositionstart', compositionStart)
    element.addEventListener('compositionend', compositionEnd)
    element.addEventListener('input', reconcile)
    element.addEventListener('copy', copy)
    element.addEventListener('cut', copy)
    document.addEventListener('selectionchange', syncSelection)
    element.addEventListener('beforeinput', input)
    element.addEventListener('keydown', key)
    element.addEventListener('paste', paste)
    const endPlainPaste = () => { pastePlain.current = false }
    for (const type of ['keyup', 'focusout', 'compositionstart']) element.addEventListener(type, endPlainPaste)
    return () => {
      for (const type of ['keyup', 'focusout', 'compositionstart']) element.removeEventListener(type, endPlainPaste)
      observer.disconnect()
      element.removeEventListener('compositionstart', compositionStart)
      element.removeEventListener('compositionend', compositionEnd)
      element.removeEventListener('dragstart', dragStart)
      element.removeEventListener('dragover', dragOver)
      element.removeEventListener('drop', dropListener)
      element.removeEventListener('dragend', dragEnd)
      element.removeEventListener('input', relayFieldInput)
      element.removeEventListener('input', reconcile)
      element.removeEventListener('copy', copy)
      element.removeEventListener('cut', copy)
      document.removeEventListener('selectionchange', syncSelection)
      element.removeEventListener('beforeinput', input)
      element.removeEventListener('keydown', key)
      element.removeEventListener('paste', paste)
    }
  })
  // A control or a click elsewhere (a toggle, Copy, a menu) and focus leaving the editor act on the document with the
  // composed text in it. Registered once per root; `ending` is the latest render's endComposition.
  useEffect(() => {
    if (!rootElement) return () => {}
    const pointerOutside = (event: Event) => {
      const active = composing.current
      if (active && !(event.target instanceof Node && active.container.contains(event.target))) ending.current('finish')
    }
    const focusOut = (event: FocusEvent) => {
      if (!(event.relatedTarget instanceof Node && rootElement.contains(event.relatedTarget))) ending.current('finish')
    }
    document.addEventListener('pointerdown', pointerOutside, true)
    document.addEventListener('click', pointerOutside, true)
    rootElement.addEventListener('focusout', focusOut)
    return () => {
      document.removeEventListener('pointerdown', pointerOutside, true)
      document.removeEventListener('click', pointerOutside, true)
      rootElement.removeEventListener('focusout', focusOut)
    }
  }, [rootElement, composing, ending])
  useLayoutEffect(() => { importedSinceRender.current = false; renderedSnapshot.current = null })
  useLayoutEffect(() => {
    if (state !== current.current || composing.current) return
    const rendered = renderedState.current?.state
    renderedState.current = { state, version: state.doc.version() }
    if (mode === 'review' || !root.current || !state.selection) return
    // A render that did not commit a new state is not a selection change: the DOM selection may hold a native change
    // whose `selectionchange` is still queued. Adopt it instead of overwriting it; only a lost DOM selection is restored.
    const native = window.getSelection()
    if (rendered === state && native?.anchorNode && root.current.contains(native.anchorNode) && native.focusNode && root.current.contains(native.focusNode)) { syncSelection(); return }
    if (document.activeElement?.matches('input, textarea, select, [data-core-field]')) return
    // Restoring a background editor's selection must not focus it over the
    // editable host the user is currently typing in.
    if (document.activeElement instanceof HTMLElement && document.activeElement.isContentEditable &&
      !root.current.contains(document.activeElement)) return
    restoring.current = true
    try {
      // A block-level gap renders as the DOM child gap of its container.
      const dom = (point: CorePoint) => {
        const gap = gapOf(state.blocks, point)
        return gap ? { blockId: gap.parent?.id ?? 'root', offset: gap.index } : point
      }
      const anchor = domPoint(editingRoot() ?? root.current, dom(state.selection.anchor), state.blocks)
      const focus = domPoint(editingRoot() ?? root.current, dom(state.selection.focus), state.blocks)
      // Importing a native selection must preserve its live Range and browser-owned selection gesture.
      // Only editor-driven changes or a lost/moved DOM selection need a native selection write.
      if (native && (native.anchorNode !== anchor.node || native.anchorOffset !== anchor.offset ||
        native.focusNode !== focus.node || native.focusOffset !== focus.offset)) {
        native.setBaseAndExtent(anchor.node, anchor.offset, focus.node, focus.offset)
      }
      const cursor = lexicalCursor.current
      if (cursor) {
        const boundary = dom(state.selection.focus).offset
        const owned = domChildren(root.current)
        const adjacent = owned[boundary] ?? owned[boundary - 1]
        const bounds = adjacent?.getBoundingClientRect()
        if (bounds) Object.assign(cursor.style, { left: `${bounds.left}px`,
          top: `${owned[boundary] ? bounds.top : bounds.bottom}px`, width: `${bounds.width}px` })
      }
      scrollCaret()

    } finally { restoring.current = false }
  })
  function scrollCaret() {
    const selection = window.getSelection()
    if (!selection?.focusNode || !root.current?.contains(selection.focusNode)) return
    const range = document.createRange()
    range.setStart(selection.focusNode, selection.focusOffset)
    range.collapse(true)
    for (let parent: HTMLElement | null = root.current; parent; parent = parent.parentElement) {
      if (!/(auto|scroll)/u.test(parent.style.overflow + parent.style.overflowY)) continue
      const bounds = parent.getBoundingClientRect()
      const rect = range.getBoundingClientRect()
      if (rect.bottom > bounds.bottom) parent.scrollBy(0, rect.bottom - bounds.bottom)
      else if (rect.top < bounds.top) parent.scrollBy(0, rect.top - bounds.top)
    }
    const rect = range.getBoundingClientRect()
    const bottom = window.visualViewport ? window.visualViewport.offsetTop + window.visualViewport.height : window.innerHeight
    if (rect.bottom > bottom) window.scrollBy(0, rect.bottom - bottom)
    else if (rect.top < 0) window.scrollBy(0, rect.top)
  }
  useLayoutEffect(() => {
    window.visualViewport?.addEventListener('resize', scrollCaret)
    return () => { window.visualViewport?.removeEventListener('resize', scrollCaret) }
  })
  const documentBlocks = scopeBlocks(state.doc)
  const showEmptyState = mode === 'edit' && editable && documentBlocks.every(block => block.type === 'pageTitle' || block.type === 'pageMeta' || block.type === 'p' && children(block).length === 0)
  const firstBodyBlockId = documentBlocks.find(block => block.type !== 'pageTitle' && block.type !== 'pageMeta')?.id
  // The empty state is overlay chrome (spec :uc[cbe-ux-contextual-placeholders]) anchored at the first body block,
  // so a title above the body never sits under it; a title rewrap resizes the editor and re-places it.
  useLayoutEffect(() => {
    const host = surface.current
    const editor = root.current
    const place = () => {
      if (!showEmptyState || !host || !editor) return
      const body = firstBodyBlockId ? editor.querySelector(`[data-block-id="${firstBodyBlockId}"]`) : null
      const top = (body?.getBoundingClientRect().top ?? editor.getBoundingClientRect().bottom) - host.getBoundingClientRect().top
      host.style.setProperty('--content-blocks-empty-state-top', `${top}px`)
    }
    place()
    const observer = new ResizeObserver(place)
    if (showEmptyState && editor) observer.observe(editor)
    return () => { observer.disconnect() }
  }, [showEmptyState, firstBodyBlockId, root])
  const selectedCells = selectedCellIds(state)
  function selectedCell(id: string) { return selectedCells.has(id) }
  function tablePointer(target: EventTarget, moving: boolean) {
    const element = target instanceof Element ? target.closest<HTMLElement>('[data-block-id]') : null
    const cell = element?.closest<HTMLTableCellElement>('td,th')
    if (!moving) tableDragging.current = !!cell
    if (!cell) return
    const table = findBlock(current.current.blocks, cell.closest('table')?.dataset.blockId ?? '')
    if (table?.type !== 'table') return
    const grid = tableGrid(table)
    const y = grid.findIndex((row) => row.some((item) => item.id === cell.dataset.blockId))
    const x = grid[y]?.findIndex((item) => item.id === cell.dataset.blockId) ?? -1
    if (x < 0 || y < 0) return
    const focus = { blockId: cell.dataset.blockId ?? '', offset: 0 }
    if (!moving) {
      commit({ state: { ...current.current, selection: { anchor: focus, focus,
        cell: { tableId: table.id, x, y } } }, change: null })
      return
    }
    const selection = current.current.selection
    const anchor = selection?.cell
    if (!tableDragging.current || !anchor || anchor.tableId !== table.id ||
      anchor.x === x && anchor.y === y) return
    // The anchor and focus cells; the rectangle they span is core `tableShape`.
    commit({ state: { ...current.current, selection: { anchor: selection.anchor, focus,
      cell: anchor, table: { tableId: table.id, anchor: { x: anchor.x, y: anchor.y }, focus: { x, y } },
    } }, change: null })
  }
  const practice = mode === 'answer' && props.answerActiveQid === undefined ? props.answerPractice : undefined
  const practiceMarks = practice?.marks
  // The whole document, not `state.blocks`: a press inside a question field scopes `state.blocks` to that field.
  const practiceHidden = practice?.hidden?.(scopeBlocks(state.doc))
  function renderBlock(block: Block, todo = false, inline = false, atRowEnd = true): ReactNode {
    if (block.type === 'chars' || block.type === 'text') {
      const props = { 'data-block-id': block.id, ...(mode === 'edit' && isListed(documentBlocks, block) ? { draggable: true } : {}) }
      const text = plain(block)
      let content: ReactNode = mode === 'review' || !text.includes('\n') ? text : text.split('\n').flatMap((line, index) =>
        index === 0 ? [line] : [createElement('br', { key: index }), line])
      const format = block.type === 'chars' ? block.format ?? 0 : 0
      const marks = format ? ([[1, 'strong', 'bold'], [2, 'em', 'italic'], [8, 'u', 'underline'], [4, 's', 'strikethrough'],
        [16, 'code', 'code']] as const).filter(([bit]) => format & bit) : []
      const caretLine = mode !== 'review' && atRowEnd && text.endsWith('\n') && <br />
      // The outermost mark is the leaf element itself (prosemirror-view's mark views wrap the text directly); only an
      // unformatted leaf needs a span to own its text.
      for (const [index, [, tag, style]] of marks.entries()) {
        content = index < marks.length - 1 ? createElement(tag, { className: blockEditorTheme.text[style] }, content)
          : createElement(tag, { key: block.id, ...props, 'data-leaf': 'true', className: blockEditorTheme.text[style] }, content, caretLine)
      }
      if (marks.length > 0) return content
      return <span key={block.id} {...props} data-leaf="true">{content}{caretLine}</span>
    }
    const props = { 'data-block-id': block.id, className: block.type === 'p' ? blockEditorTheme.paragraph : block.type === 'h' ? blockEditorTheme.heading[`h${block.level}`] : block.type === 'quote' ? blockEditorTheme.quote : block.type === 'list' ? (block.subtype === 'ordered' ? blockEditorTheme.list.ol : blockEditorTheme.list.ul) : block.type === 'a' ? blockEditorTheme.link : block.type === 'hr' ? blockEditorTheme.hr : undefined, ...(mode === 'edit' && isListed(documentBlocks, block) ? { draggable: true } : {}) }
    const renderedChildren = children(block)
    const renderChild = (child: Block, index: number) => {
      const end = (row || atRowEnd) && index === renderedChildren.length - 1
      // Compact list/quote prose still owns a row. Dropping its element loses
      // the DOM gap after a break and maps the next edit to the container edge.
      if (child.type === 'p' && child.children.length > 0 &&
        (block.type === 'li' && index === 0 || block.type === 'quote' && renderedChildren.length === 1)) {
        return <span key={child.id} data-block-id={child.id}>
          {child.children.map((part, index) => renderBlock(part, false, true, end && index === child.children.length - 1))}
          {mode !== 'review' && child.children.at(-1)?.type === 'br' && <br />}
        </span>
      }
      return renderBlock(child, block.type === 'list' && block.subtype === 'todo',
        inline || ['p', 'h', 'pre', 'code', 'td'].includes(block.type), end)
    }
    const row = ['p', 'h', 'li', 'quote', 'td'].includes(block.type)
    // Lexical `$reconcileElementTerminatingLineBreak`: an empty row or one ending in a line break gets a managed
    // `<br>`, so the browser can render a caret there.
    const trailingBreak = mode !== 'review' && row && (children(block).length === 0 || children(block).at(-1)?.type === 'br')
    // Preserve a DOM position for selection mapping without an editable line.
    if (block.type === 'comments') return <div key={`${documentEpoch}:${block.id}`} data-block-id={block.id} hidden contentEditable={false} />
    // A block the practising host leaves out (AnswerPractice.hidden) does not render, nor do its parts.
    if (practiceHidden?.has(block.id)) return null
    if (block.type === 'p') return renderParagraph(block, api, [
      renderedChildren.map((child, index) => renderChild(child, index)),
      trailingBreak && <br key="caret-line" />,
    ], `${documentEpoch}:${block.id}`, practiceMarks)
    // Authored diff wrappers keep their presentation in edit/read/answer modes too.
    const BlockComponent = block.type === 'diff' && mode !== 'review' ? undefined
      : reviewBlockViews[block.type] ?? blockViews[block.type]
    if (block.type === 'list' && block.subtype === 'toggle') return <ToggleList key={`${documentEpoch}:${block.id}`} block={block} api={api} draggable={props.draggable} />
    if (BlockComponent) return <BlockComponent key={`${documentEpoch}:${block.id}`} block={block} api={api}>
      {renderedChildren.map((child, index) => renderChild(child, index))}
      {trailingBreak && <br key="caret-line" />}
    </BlockComponent>
    if (block.type === 'criticComment') {
      const source = blocksToMd([block])
      const attributes = { ...props, contentEditable: false, 'data-testid': `markdown-node-fallback-${block.id}`, 'aria-label': 'criticComment fallback' }
      return inline
        ? <span key={block.id} {...attributes}>{source}</span>
        : <div key={block.id} {...attributes} className="border-border bg-surface-2 my-3 grid gap-1 rounded-lg border px-3 py-2">
          <strong className="text-label-small text-on-surface-secondary">criticComment</strong><div className="whitespace-pre-wrap">{source}</div>
        </div>
    }
    if (block.type === 'definition') return null
    if (block.type === 'br') return <br key={block.id} {...props} />
    // One core unit: a non-editable chip, so the caret stops only at its edges. The payload carries
    // the atom's typed identity into copied HTML, as Lexical's inline text nodes do.
    if (inline && isAtom(block)) return <span key={block.id} {...props} contentEditable={false}
      data-inline-presentation-type={block.type} data-inline-text-payload={JSON.stringify({ block })}>
      {atomLabel(block)}</span>
    if (block.type === 'hr') return <hr key={block.id} {...props}
      style={state.selection?.nodes?.includes(block.id) ? { outline: '2px solid Highlight' } : undefined} />
    if (block.type === 'img') return <img key={block.id} {...props} src={block.src}
      alt={block.alt ?? ''} />
    if (block.type === 'video' || block.type === 'audio') {
      return createElement(block.type, { key: block.id, ...props, src: block.src, controls: true })
    }
    const content = renderedChildren.map((child, index) => renderChild(child, index))
    if (trailingBreak) content.push(<br key="caret-line" />)
    if (block.type === 'diff') return createElement(inline ? 'span' : 'div', {
      key: block.id, ...props, 'data-diff-kind': block.kind, className: diffClass(block.kind, inline),
    }, content)
    if (block.type === 'li') return <li key={block.id} {...props}
      className={[blockEditorTheme.list.listitem, todo ? block.done ? blockEditorTheme.list.listitemChecked : blockEditorTheme.list.listitemUnchecked : '', children(block).some(child => child.type === 'list') ? blockEditorTheme.list.nested.listitem : ''].filter(Boolean).join(' ')}
      role={todo ? 'checkbox' : undefined} aria-checked={todo ? !!block.done : undefined}
      tabIndex={todo ? -1 : undefined}>
      {todo && <span contentEditable={false} aria-hidden="true" className="pointer-events-none absolute top-0.5 left-0">
        <Checkbox checked={!!block.done} readOnly tabIndex={-1} render={<span />} />
      </span>}
      {content}</li>
    if (block.type === 'pre') return <div key={block.id}>
      <label contentEditable={false}>Code language<input aria-label="Code language"
        value={block.lang ?? ''} onInput={(event) => {
          dispatch({ type: 'setCodeLanguage', block: block.id, language: event.currentTarget.value.toLowerCase() })
        }} /></label>
      <button type="button" contentEditable={false} title="Copy code" onClick={() => {
        void navigator.clipboard.writeText(plain(block))
      }}>Copy code</button>
      <pre {...props} data-presentation-type={block.latex ? "pre" : undefined} data-content-blocks-code-source=""><code>{content}</code></pre></div>
    if (block.type === 'table') return <div key={block.id} style={{ overflowX: 'auto', overflowY: 'hidden', borderRadius: 28 }}>
      <table {...props}><tbody>{content}</tbody></table></div>
    if (block.type === 'td') return <td key={block.id} {...props} rowSpan={block.rowSpan} colSpan={block.colSpan}
      className={selectedCell(block.id) ? 'cb-table-cell-selected' : undefined}
      style={{ border: '1px solid GrayText', minWidth: 80, padding: 8, background: selectedCell(block.id) ? 'Highlight' : undefined }}>
      {children(block).some((child) => child.type === 'hr') ? content : <p>{content}</p>}</td>
    const tag = block.type === 'h' ? `h${block.level}`
      : block.type === 'list' ? block.subtype === 'ordered' ? 'ol' : 'ul'
      : block.type === 'quote' ? 'blockquote'
      : ['p', 'tr', 'code', 'a'].includes(block.type) ? block.type : 'div'
    return createElement(tag, { key: block.id, ...props,
      ...(block.type === 'list' && block.subtype === 'ordered' ? { start: block.start ?? 1 } : {}),
      ...(block.type === 'code' ? { 'data-inline-presentation-type': 'code' } : {}),
      ...(block.type === 'a' ? { href: block.href, onClick: (event: { preventDefault: () => void }) => { event.preventDefault() } } : {}),
 }, content)
  }
  const selection = state.selection
  const anchorGap = !state.scope && selection ? gapOf(state.blocks, selection.anchor) : undefined
  const caretGap = selection && !selection.nodes?.length && anchorGap?.siblings === state.blocks &&
    gapOf(state.blocks, selection.focus)?.index === anchorGap.index ? anchorGap.index : undefined
  return <EditorContext value={handle}><EditorLayoutContext value={layout}>
    <EditorModeProvider mode={mode} activeQid={props.answerActiveQid} answerAutoFocus={props.answerActiveQid !== undefined && props.answerAutoFocus !== false} clozeOrder={props.answerActiveQid === undefined ? undefined : collectClozeQids(state.blocks)}>
    <AnswerPracticeProvider practice={practice}
      layout={practice ? practiceLayout(scopeBlocks(state.doc), practiceHidden) : undefined}>
    <ClozeHintFailProvider onFail={props.onClozeHintFail}><MediaUrlProvider previews={mediaPreviews} fileHandler={fileHandler}>
    <PageInfoProvider spaceId={props.spaceId ?? 'editor'} resolvePage={props.resolvePage} onOpenPage={props.onOpenPage}>
    <ReviewProvider renderEditor={renderNestedEditor} review={props.review} comments={props.comments} spaceId={props.spaceId} resolvePage={props.resolvePage}>
    <MobileReviewProvider container={mode === 'answer' ? null : props.review?.mobilePanelContainer}><NestedEditorProvider renderCodeEditor={renderNestedCode}><BlockViewProvider value={{ updateBlock, upload, renderQuestionSettings: props.renderQuestionSettings }}><div ref={surface} className={`relative isolate text-on-surface ${props.review && mode !== 'answer' ? 'min-[1026px]:data-[review-comment-pending]:pe-[344px]' : ''} ${props.comments?.length && mode !== 'answer' ? 'min-[1026px]:pe-[344px]' : ''}`} data-content-blocks-editor-surface="true" data-editor-layout={layout}>
    {props.header}
    {/* The root is `plaintext-only`: React saves and restores the selection around every commit by walking all text of a
      focused `contenteditable="true"` element (page-sized work per key); it skips other values. Input stays ours: keys at
      keydown (core/keymap.ts), text and composition at beforeinput, paste and drop by their events. Selectors: EDITABLE. */}
    {/* White space is preserved for typed spaces with break-spaces, as ProseMirror ships it (prosemirror-view/style/prosemirror.css:7-8, Tiptap core style.ts:8): paint per keystroke on 2,000 blocks measured 31 ms under pre-wrap vs 1.9 ms (exploratory, cause unproven; docs/benchmarks/sync/editor/README.md, Tried). */}
    {/* oxlint-disable-next-line jsx-a11y/click-events-have-key-events -- Contenteditable textbox has a native keydown owner; synthetic click must run after child React handlers to preserve field focus. */}
    <div ref={attachRoot} data-testid={testIds.root} className={`${props.className ?? ''} [&_[data-linked-block]]:ring-2 [&_[data-linked-block]]:ring-primary`} style={{ whiteSpace: 'break-spaces', wordBreak: 'break-word' }} onPointerDown={(event) => { if (!event.shiftKey) tablePointer(event.target, false) }}
      onPointerMove={(event) => { if (event.buttons === 1) tablePointer(event.target, true) }}
      onPointerUp={() => { tableDragging.current = false }} contentEditable={mode === 'edit' && editable ? 'plaintext-only' : false} suppressContentEditableWarning role="textbox"
      aria-multiline="true" tabIndex={0} onClick={(event) => {
        const target = event.target
        if (mode !== 'edit' || !(target instanceof Element) || target.closest('input, textarea, button, [data-core-field]')) return
        const checkbox = target.closest<HTMLElement>('li[role="checkbox"]')
        if (target === checkbox && event.clientX > 0 &&
          event.clientX - checkbox.getBoundingClientRect().left < 20) {
          dispatch({ type: 'toggleTodo', item: checkbox.dataset.blockId ?? '' })
          checkbox.focus()
          return
        }
        root.current?.focus()
        if (event.shiftKey) { syncSelection(); return }
        const dom = window.getSelection()
        if (event.detail >= 3) {
          const id = target.closest<HTMLElement>('[data-block-id]')?.dataset.blockId
          const entries = rows(current.current.blocks)
          // The row holding the clicked block; a block outside the scope's rows (an inline question, a field) names none.
          const row = id ? entries.find(entry => findBlock([entry.block], id)) : undefined
          if (row && !plain(row.block).includes('\n')) {
            commit({ state: { ...current.current, selection: { anchor: pointAt(row, 0),
              focus: pointAt(row, plain(row.block).length) } }, change: null })
            return
          }
        }
        if (event.detail === 2 && dom?.anchorNode) {
          syncSelection()
          // Chrome already selected the clicked word. Its focus is the exclusive
          // end, which may belong to punctuation or the next word.
          if (!dom.isCollapsed) return
          const entries = rows(current.current.blocks)
          const selection = current.current.selection
          if (selection) {
            const position = locate(entries, selection.focus)
            const row = entries[position.row]
            const segment = wordSegments(plain(row.block)).find(({ index, segment }) =>
              index <= position.offset && position.offset < index + segment.length)
            if (segment) commit({ state: { ...current.current, selection: {
              anchor: pointAt(row, segment.index),
              focus: pointAt(row, segment.index + segment.segment.length),
            } }, change: null })
          }
          return
        }
        if (dom?.anchorNode && target.contains(dom.anchorNode)) {
          syncSelection()
          return
        }
        const block = target.closest<HTMLElement>('[data-block-id]')
        if (!block?.dataset.blockId || !root.current) return
        const point = domPoint(root.current, { blockId: block.dataset.blockId, offset: 0 })
        dom?.setBaseAndExtent(point.node, point.offset, point.node, point.offset)
        syncSelection()
      }}>
      {(props.pageTitle ?? pageDocument(state.doc).title) !== undefined && !documentBlocks.some(block => block.type === 'pageMeta' || block.type === 'pageTitle') && <>
        {props.pageComments && !props.pageComments.composer && props.pageComments.threads.length === 0 && mode !== 'answer' && <div contentEditable={false} className="mb-3"><PageCommentAction onClick={() => { setPageCommentsComposing(true) }} /></div>}
        {renderBlock({ type: 'pageTitle', id: PAGE_TITLE_BLOCK_ID, title: props.pageTitle ?? pageDocument(state.doc).title ?? '' })}
        {props.pageComments && mode !== 'answer' && <PageComments config={props.pageComments} composing={pageCommentsComposing} onComposingChange={setPageCommentsComposing} />}
      </>}
      {(frozen ? documentBlocks.map(live => live.id === frozen.id ? frozen : live) : documentBlocks).map((block, index) => block.type === 'pageTitle' || block.type === 'pageMeta' ? <Fragment key={block.id}>
        {props.pageComments && !props.pageComments.composer && props.pageComments.threads.length === 0 && mode !== 'answer' && <div contentEditable={false} className="mb-3"><PageCommentAction onClick={() => { setPageCommentsComposing(true) }} /></div>}
        {renderBlock(block)}
        {props.pageComments && mode !== 'answer' && <PageComments config={props.pageComments} composing={pageCommentsComposing} onComposingChange={setPageCommentsComposing} />}
      </Fragment> : mode !== 'review' && isTextRow(block) ? <TextRow key={`${documentEpoch}:${block.id}${remounted?.id === block.id ? `:${remounted.count}` : ''}`} block={block} mode={mode}
        viewMode={api.mode} listed={state.blocks === documentBlocks ? block === documentBlocks[index] : isListed(state.blocks, block)}
        inDocument={block === documentBlocks[index]} render={renderBlock} practiceMarks={practiceMarks} />
        : remounted?.id === block.id ? <Fragment key={`${documentEpoch}:${block.id}:${remounted.count}`}>{renderBlock(block)}</Fragment> : renderBlock(block))}
      {/* Lexical paints its block cursor only in an editable document. */}
      {mode === 'edit' && editable && caretGap !== undefined && [state.blocks[caretGap], state.blocks[caretGap - 1]].some(block => block && ['atom', 'grid'].includes(blockClass(block))) &&
        <span ref={lexicalCursor} data-lexical-cursor="true" contentEditable={false} aria-hidden="true"
          style={{ position: 'fixed', borderTop: '2px solid currentColor', pointerEvents: 'none' }} />}
      </div>
      {showEmptyState &&
        (props.placeholder === undefined ? <div className="border-border bg-surface-1 text-on-surface-secondary pointer-events-none absolute inset-x-0 top-(--content-blocks-empty-state-top) rounded-xl border border-dashed px-5 py-4" data-testid="content-blocks-editor-empty-state">
          <p className="text-on-surface text-sm font-medium">Start with an idea</p><p className="mt-1 text-sm">{"Write, or type '/' for commands"}</p>
        </div> : props.placeholder)}
      <RemoteSelections state={state} root={rootElement} store={remoteSelectionsStore(handle)} composingOwner={frozen?.id} />
      {overlays.map(Overlay => <Overlay key={`${documentEpoch}:${Overlay.name}`} api={api} />)}
      {reviewOverlays.map(Overlay => <Overlay key={`${documentEpoch}:${Overlay.name}`} api={api} />)}
      {props.children}
  </div></BlockViewProvider></NestedEditorProvider></MobileReviewProvider></ReviewProvider></PageInfoProvider></MediaUrlProvider></ClozeHintFailProvider></AnswerPracticeProvider></EditorModeProvider></EditorLayoutContext></EditorContext>
}

/** Input types an IME session sends (Input Events Level 2). */
const compositionInputs = new Set(['insertCompositionText', 'deleteCompositionText', 'insertFromComposition', 'deleteByComposition'])

function containerLength(container: HTMLElement, node: Node, offset: number) {
  const range = document.createRange()
  range.setStart(container, 0)
  range.setEnd(node, offset)
  return range.toString().length
}

const inputTypes = new Set(['toggleTodo', 'duplicateBlock', 'insertParagraph',
  'insertLineBreak', 'deleteContentBackward',
  'deleteContentForward', 'deleteWordBackward', 'deleteWordForward', 'deleteSoftLineBackward',
  'deleteSoftLineForward', 'formatBold', 'formatItalic', 'formatUnderline',
  'formatStrikeThrough', 'historyUndo', 'historyRedo'])

/** Visible chip text of an atom; its identity lives in the chip payload. */
function atomLabel(block: Block): string {
  if (block.type === 'mention') return block.subtype === 'date' ? new Date(block.date).toLocaleDateString() : `@${block.uid}`
  if (block.type === 'pages') return block.ids.join(', ')
  if (block.type === 'page' || block.type === 'pageLink') return block.pageId
  if (block.type === 'img') return block.alt?.length ? block.alt : block.src
  if (block.type === 'file') return block.name ? block.name : block.src
  return 'src' in block ? block.src : ''
}

/** Review comment and source editors: a module function, so the review context keeps its identity across renders. */
function renderNestedEditor(nestedProps: CoreEditorProps) {
  // Source editors retain the browser focus outline used by the original shell.
  const sourceEditor = pageDocument(nestedProps.doc).content.children[0]?.type === 'pre'
  return <CoreEditor {...nestedProps} className={sourceEditor ? 'text-body-small min-h-24 max-w-none' : nestedProps.className?.replace(' outline-none', '')} />
}

/**
 * Each question's answer parts (cloze or question blocks) in document order, leaving out hidden blocks; a question is
 * its parts' qid. Its controls render after its last part.
 */
function practiceLayout(blocks: readonly Block[], hidden?: ReadonlySet<string>): PracticeLayout {
  const parts = new Map<string, Block[]>()
  const visit = (items: readonly Block[]) => {
    for (const block of items) {
      if (hidden?.has(block.id)) continue
      if (block.type === 'cloze' || block.type === 'question') {
        parts.set(block.qid, [...parts.get(block.qid) ?? [], block])
      }
      visit(children(block))
    }
  }
  visit(blocks)
  return { parts }
}

function collectClozeQids(blocks: readonly Block[]): string[] {
  const result: string[] = []
  for (const block of blocks) {
    if (block.type === 'cloze') result.push(block.qid)
    result.push(...collectClozeQids(children(block)))
  }
  return result
}

/** Reveal the URL target once; later editing retains normal editor behavior. */
function revealLinkedBlock(root: HTMLElement | null, blockId: string | undefined) {
  const element = blockId && [...(root?.querySelectorAll<HTMLElement>('[data-block-id]') ?? [])]
    .find(element => element.dataset.blockId === blockId)
  if (!element || !root) return () => {}
  element.dataset.linkedBlock = 'true'
  root.dataset.linkedBlockId = blockId
  element.scrollIntoView({ block: 'center' })
  return () => { delete element.dataset.linkedBlock; delete root.dataset.linkedBlockId }
}
